// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Collapse,
  CardMedia,
  Divider,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalAlertError,
  ModalContentSwitch,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const STOP_LOSS_TRAILING_ACTIVATION_POSITION_HELP_IMAGE = "/illustrations/converted/stop_loss_trailings/activation-position.png";
const STOP_LOSS_TRAILING_ACTIVATION_POSITION_HELP_DESCRIPTION = (
  <>
    Similar to the take profit dynamic feature, it is possible to configure up to 50 stop loss trailing ranges. A range is defined by its activation
    position. The activation position is a reference to the initial stop loss distance, which is defined by the difference between the first stop loss
    and the trade opening price. The initial stop loss difference is always a positive value irrespective of whether a long or short trade is
    executed. This initial value represents the unitless reference of 100. 0 is exactly the price the initial stop loss was set to and 100 is the
    opening price of the order. Interpolating this principle, every price above (long) or below (short) the opening price is a value above 100. By
    using this principle it is possible to treat a trade entirely independent from fixed values such as pip count.
    <br />
    <br />
    So, when setting for example 30 as activation position, this means that when the price is increasing (short) or decreasing (long) a significant
    amount, it will activate the configured stop loss trailing stage.{" "}
    <b>A stop loss trailing stage is active until the end of the trade or if another stage is activated later on. </b>Based on this logic, an
    activation position must be above 0 and theoretically has no upper limit, though practical values would typically be between 0 and the initially
    configured take profit level.
  </>
);

const STOP_LOSS_TRAILING_TYPES_HELP_SECTION = {
  0: {
    image: null,
    description: (
      <>
        A stage configured as <b>Off</b> disables dynamic modification of the stop loss level. This option allows you to deactivate stop loss
        adjustments at specific points during a trade. For example, after a successful stop loss modification, you may want to prevent further changes
        to the stop loss level by activating an Off-stage.
      </>
    ),
    imageHeight: "450px",
  },
  1: {
    image: null,
    description: (
      <>
        Using a fixed amount of pips represents the most straightforward approach for defining stop loss trailing behavior. When this stage activates,
        the stop loss difference present at that moment establishes the fixed pip distance for all subsequent trailing movements.
        <br />
        <br />
        The reference value depends on when the stage becomes active: if it's the first stage to activate, the initial stop loss difference serves as
        the baseline. However, if this stage follows another trailing method, the stop loss distance at the conclusion of the previous stage becomes
        the new reference value for the fixed pip trailing.
      </>
    ),
    imageHeight: "450px",
  },
  2: {
    image: "/illustrations/converted/stop_loss_trailings/type-breakeven.png",
    description: (
      <>
        The breakeven trailing method implies that the stop loss value is only modified once at a specific price. This ensures that already-made
        profits are secured. The sudden jump in stop loss will occur when the trade is already in a respectable profitable zone, and it's not expected
        that the trade will change its direction. You can use the <b>Breakeven position</b> input parameter to define the moment when the jump will
        occur.
        <br />
        <br />
        The initial distance between the opening price and initial stop loss represents the reference value 100. Let's assume this was 80 pips. If the{" "}
        <b>Breakeven position</b> is set to 150, the jump to breakeven will occur when the price rises 40 pips above the opening price. The larger the
        breakeven position, the later the jump.
      </>
    ),
    imageHeight: "241px",
  },
  3: {
    image: "/illustrations/converted/stop_loss_trailings/type-market.png",
    description: (
      <>
        Market trailing doesn't rely on complex algorithms. Instead, it uses the open and close prices of the previous candle sticks, combined with
        the spread, to calculate its movements. The only adjustable variable is a buffer added to the stop loss distance. This buffer can be
        calculated by two parameters: a fixed value measured in pips (<b>Market buffer fix</b>) or/and a dynamic value based on the current spread (
        <b>Market buffer spread</b>). Based on the spread, the dynamic option is handy to avoid being stopped during night hours.
        <br />
        <br />
        For instance, the ask price is crucial for long trades to define when a trade will close. However, the stop loss level might come too close if
        the spread is too large. Therefore, during phases of large spread, the market trailing variant can automatically adapt the buffer to better
        react in volatile phases or during night hours. Both parameters move the stop loss line further away from the actual price. Both buffer types
        can be combined freely. If used together, their values will be added to each other.
      </>
    ),
    imageHeight: "257px",
  },
  4: {
    image: null,
    description: (
      <>
        This stop loss method enables the trading module to use an indicator value for calculating the stop loss level. Only indicators configured
        with <b>Forward</b> for <b>Signal used for</b> can be selected. This method is particularly useful when implementing a custom algorithm to
        determine stop loss levels, which can be achieved by configuring a custom indicator or custom oscillator.
        <br />
        <br />
        When using predefined indicator algorithms, a distinction is made between indicators that move along the chart and oscillators that derive
        values around a fixed reference. For indicators, the value is used directly as the stop loss level. For oscillators, the value is added to
        (short) or subtracted (long) from the current price level to determine the stop loss position.
        <br />
        <br />
        To modify the multiplier when using an oscillator, configure the <b>Forward adjustment</b> in your indicator settings. The Average True Range
        and Parabolic SAR algorithms are among the most commonly utilized indicators for stop loss adjustments.
      </>
    ),
    imageHeight: "450px",
  },
  5: {
    image: null,
    description: (
      <>
        A chart analyzer signal generator provides a powerful method for detecting support and resistance levels. These levels often represent optimal
        values for stop loss placement. This option enables the trading algorithm to automatically select one of the forwarded resistance or support
        levels as the stop loss target.
        <br />
        <br />
        Only chart analyzers configured with <b>Forward</b> for <b>Signal used for</b> can be selected. To filter and select only specific support and
        resistance values, configure the <b>Supports and resistances forward option</b> in your chart analyzer settings.
        <br />
        <br />
        In some cases, the forwarded resistance or support level may be too close or too far away from the current price. To define a valid range, you
        can use the following parameters:
        <ul>
          <li>Support/resistance fix pips minimum</li>
          <li>Support/resistance rate of change minimum</li>
          <li>Support/resistance fix pips maximum</li>
          <li>Support/resistance rate of change maximum</li>
        </ul>
        Only if the support or resistance lies within these limits will it be selected as the stop loss target. In contrast to selecting the initial
        stop loss level using a chart analyzer,{" "}
        <b>the stop loss will switch to a closer stop loss level when a respective support or resistance occurs</b>.
      </>
    ),
    imageHeight: "450px",
  },
  6: {
    image: "/illustrations/converted/stop_loss_trailings/type-stepwise.png",
    description: (
      <>
        The stepwise trailing method incrementally moves the stop loss closer to the current price. Each step is of equal size, and the next step is
        executed when the price increases by a specified value. As is customary in this application, the input parameters for this trailing method are
        defined with relative parameters. In this case, the initial stop loss difference, the price difference between the opening price and the
        initially configured stop loss, represents the unitless reference value of 100. The input parameter <b>Steps size</b> determines the size of
        each step, while <b>Steps position</b> indicates when the next step will be executed. As previously mentioned, both values are relative.
        <br />
        <br />
        For instance, if the initial stop loss difference is 100 pips and <b>Steps size</b> is set to 130, the next step will occur when the price
        reaches initial stop loss + 130 pips. Subsequently, the next step will be executed at initial stop loss + 260 pips. This pattern will continue
        until the trade is eventually stopped out. If <b>Steps position</b> is set to 80, the stop loss value will increase to initial stop loss value
        + 80 pips at the first step and to initial stop loss value + 160 pips at subsequent steps. This pattern will also repeat until the trade is
        stopped out. The illustration below demonstrates the function principle and provides two examples of the operation of this stop loss trailing
        method.
      </>
    ),
    imageHeight: "271px",
  },
  7: {
    image: "/illustrations/converted/stop_loss_trailings/type-time.png",
    description: (
      <>
        Another method for managing closing a trade is a time-based closure. Technically, this doesn't constitute trailing since the stop loss value
        remains unchanged. Instead, when this method is selected, the trade will be closed after a predetermined time, irrespective of its current
        performance. The period is determined by the input parameters <b>Time count</b> and can be utilized in two ways.
        <br />
        <br />
        Positive values represent the period measured in bars in your current timeframes. For instance, if the value is set to 10 and you're trading
        in the M5 timeframe, the trade will be closed after 50 minutes. Conversely, negative values represent the period measured in seconds. A value
        of -3600 indicates that the trade will be closed precisely 60 minutes after it's opened, while -7200 signifies a closure time of 120 minutes
        after the trade is opened.
      </>
    ),
    imageHeight: "283px",
  },
  8: {
    image: "/illustrations/converted/stop_loss_trailings/type-linear.png",
    description: (
      <>
        The linear increase/decrease stop loss trailing method increases/decreases the stop loss after each bar in the currently active timeframe,
        resulting in a strictly linear rise/fall in the stop loss value. The initial stop loss distance is determined by the difference between the
        entry price and the initial stop loss level. This distance represents the base value of 100%. The only input parameter for this trailing
        method is the step size, defined by <b>Linear step</b>.<br />
        <br />
        The default value is 25, meaning the stop loss will increase/decrease by 2.5% of the initial stop loss distance after each closed bar. For
        instance, if the initial stop loss distance was 1000 pips, the stop loss level will increase/decrease by 25 pips after every closed bar. The
        increment might be small in some timeframes and initial stop loss configurations, so the adjustment could be smaller than the minimum
        increment for modifying the stop loss.
      </>
    ),
    imageHeight: "271px",
  },
  9: {
    image: null,
    description: (
      <>
        The dynamic increase/decrease trailing method dynamically adjusts the required distance between the current price and stop loss level. It
        starts with an initial target distance and reduces this distance by a fixed amount after each completed bar in the active timeframe.
        <br />
        <br />
        When a new bar closes, the system decreases the target distance by the specified <b>dynamic increase/decrease</b> value. The stop loss only
        moves when the current price distance exceeds this dynamically shrinking target distance.
        <br />
        <br />
        For buy trades, the method compares the current bid price minus the stop loss level against the target distance. For sell trades, it compares
        the stop loss level minus the current ask price. When the actual distance becomes larger than the target distance, the stop loss adjusts to
        maintain exactly the target distance from the current price.
        <br />
        <br />
        This creates an accelerating trailing effect - as bars close and the target distance shrinks, the stop loss moves more frequently and
        aggressively follows the price, tightening the protective distance over time.
      </>
    ),
    imageHeight: "450px",
  },
  10: {
    image: "/illustrations/converted/stop_loss_trailings/type-hilo.png",
    description: (
      <>
        With the Last high/low trailing method, the stop loss is set to the lowest local low or highest local high, depending on the direction of the
        trade. The lowest local low within a certain period, measured in bars, is used for long trades. The stop loss value is adjusted if a higher
        local low appears within the observed period. There's no limit to how often the stop loss value can change, but it can only increase for long
        trades and drease in case of short trades.
        <br />
        <br />
        The only variable that affects this trailing method is the observed period, defined by <b>High/low period</b>. This value specifies the number
        of bars preceding the current time. The larger the value, the further the stop loss is typically from the price chart. The illustration below
        demonstrates the function principle and provides some examples.
      </>
    ),
    imageHeight: "267px",
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const StopLossTrailingsDefaults = {
  nType: "0",
  nChangePosition: "0",
  nBreakevenPosition: "130",
  nMarketBufferFix: "10",
  dMarketBufferSpread: "4.0",
  nStepsSize: "80",
  nStepsPosition: "130",
  nTimeCount: "15",
  nLinearStep: "5",
  nChangingDifference: "5",
  nHiLoPeriod: "20",
  bCaSupResFixPipsMin: false,
  nCaSupResFixPipsMin: "4",
  bCaSupResRateOfChangeMin: false,
  dCaSupResRateOfChangeMin: "0.00000100",
  bCaSupResFixPipsMax: false,
  nCaSupResFixPipsMax: "400",
  bCaSupResRateOfChangeMax: false,
  dCaSupResRateOfChangeMax: "0.00001000",
  nInosUsedForValueInjection: "0",
  nCaSupResUsedForValueInjection: "0",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nChangePosition: {
    min: -1,
  },
  nBreakevenPosition: {
    min: 0,
  },
  nMarketBufferFix: {
    min: 0,
  },
  dMarketBufferSpread: {
    digits: 1,
    min: 0.0,
    step: 0.1,
  },
  nStepsSize: {
    min: 0,
  },
  nStepsPosition: {
    min: 0,
  },
  nTimeCount: {
    min: 0,
  },
  nLinearStep: {
    min: 0,
  },
  nChangingDifference: {
    min: 0,
  },
  nHiLoPeriod: {
    min: 0,
  },
  nCaSupResFixPipsMin: {
    min: 0,
  },
  dCaSupResRateOfChangeMin: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nCaSupResFixPipsMax: {
    min: 0,
  },
  dCaSupResRateOfChangeMax: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getTypeDisplay = (value) => {
  const types = {
    0: "Off",
    1: "Fix pips from inital stop loss",
    2: "Breakeven",
    3: "Market",
    4: "Indicator value",
    5: "Support/resistance from chart analyzer",
    6: "Stepwise increase/decrease",
    7: "Closing after specified time period",
    8: "Linear increase/decrease",
    9: "Dynamic increase/decrease",
    10: "Last high/low",
  };
  return types[value] || "Off";
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const StopLossTrailingsBuildName = (config) => `${getTypeDisplay(config.nType)}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const StopLossTrailingsBuildSubtitle = (config) => `Activation position: ${config.nChangePosition}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function StopLossTrailingsRenderParameters(selectedStopLossTrailing, getStopLossTrailingConfig, professionalMode, indicators, chartAnalyzers) {
  const config = getStopLossTrailingConfig(selectedStopLossTrailing);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get indicator name by ID
  const getIndicatorNameById = (indicatorId) => {
    if (!indicatorId || indicatorId === "0") return "None selected";
    if (!indicators || indicators.length === 0) return "No indicators available";

    const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
    return indicator ? `${indicator.name} (${indicator.subtitle})` : `Unknown indicator (ID: ${indicatorId})`;
  };

  // Helper function to get chart analyzer name by ID
  const getChartAnalyzerNameById = (chartAnalyzerId) => {
    if (!chartAnalyzerId || chartAnalyzerId === "0") return "None selected";
    if (!chartAnalyzers || chartAnalyzers.length === 0) return "No chart analyzers available";

    const chartAnalyzer = chartAnalyzers.find((ca) => ca.id.toString() === chartAnalyzerId.toString());
    return chartAnalyzer ? `${chartAnalyzer.name} (${chartAnalyzer.subtitle})` : `Unknown chart analyzer (ID: ${chartAnalyzerId})`;
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Activation position", config.nChangePosition)}
      {renderParam("Type", getTypeDisplay(config.nType))}
      {config.nType === "2" && renderParam("Breakeven position", config.nBreakevenPosition)}

      {config.nType === "3" && (
        <>
          {renderParam("Market buffer fix", config.nMarketBufferFix)}
          {renderParam("Market buffer spread", config.dMarketBufferSpread)}
        </>
      )}

      {config.nType === "4" && renderParam("Used indicator", getIndicatorNameById(config.nInosUsedForValueInjection))}
      {config.nType === "5" && (
        <>
          {renderParam("Used chart analyzer", getChartAnalyzerNameById(config.nCaSupResUsedForValueInjection))}
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          {config.bCaSupResFixPipsMin === true
            ? renderParam("Support/resistance fix pips minimum", config.nCaSupResFixPipsMin)
            : renderParam("Support/resistance fix pips minimum", "Off")}
          {config.bCaSupResRateOfChangeMin === true
            ? renderParam("Support/resistance rate of change minimum", config.dCaSupResRateOfChangeMin)
            : renderParam("Support/resistance rate of change minimum", "Off")}
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          {config.bCaSupResFixPipsMax === true
            ? renderParam("Support/resistance fix pips maximum", config.nCaSupResFixPipsMax)
            : renderParam("Support/resistance fix pips maximum", "Off")}
          {config.bCaSupResRateOfChangeMax === true
            ? renderParam("Support/resistance rate of change maximum", config.dCaSupResRateOfChangeMax)
            : renderParam("Support/resistance rate of change maximum", "Off")}
        </>
      )}

      {config.nType === "6" && (
        <>
          {renderParam("Steps size", config.nStepsSize)}
          {renderParam("Steps position", config.nStepsPosition)}
        </>
      )}

      {config.nType === "7" && renderParam("Time count", config.nTimeCount)}
      {config.nType === "8" && renderParam("Linear step", config.nLinearStep)}
      {config.nType === "9" && renderParam("Changing difference", config.nChangingDifference)}
      {config.nType === "10" && renderParam("High/low period", config.nHiLoPeriod)}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const StopLossTrailingsConfigure = forwardRef(
  ({ onSave, initialData, professionalMode, indicators, chartAnalyzers, initialStopLoss }, ref) => {
    // ----------------------------------------------------------------------
    // Properties
    // ----------------------------------------------------------------------
    StopLossTrailingsConfigure.propTypes = {
      onSave: PropTypes.func.isRequired,
      initialData: PropTypes.object,
      professionalMode: PropTypes.any,
      indicators: PropTypes.array,
      chartAnalyzers: PropTypes.array,
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const [scroll, setScroll] = useState("paper");
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const autoCloseTimeoutRef = useRef(null);

    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warning, setWarning] = useState(false);
    const [returnMessage, setReturnMessage] = useState("");

    const [collapseActivation, setCollapseActivation] = useState(false);
    const [collapseType, setCollapseType] = useState(false);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      openDialog: () => {
        setOpen(true);
      },
      closeDialog: () => {
        setOpen(false);
      },
    }));

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleReset = () => {
      // Clear all errors and touched state
      setErrors({});
      setTouched({});
      // Clear cross-validation error state
      setError(false);
      setReturnMessage("");

      const defaults = {
        ...StopLossTrailingsDefaults,
        nType: values.nType, // Keep the current selection
      };

      // Apply all the default values to the form
      Object.keys(defaults).forEach((key) => {
        setFieldValue(key, defaults[key]);
      });
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClose = () => {
      // Clear any existing auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      setOpen(false);
      setError(false);
      setSuccess(false);
      setWarning(false);
      setReturnMessage("");
      resetForm();
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const formik = useFormik({
      initialValues: {
        nType: initialData?.nType ?? StopLossTrailingsDefaults.nType,
        nChangePosition: initialData?.nChangePosition ?? StopLossTrailingsDefaults.nChangePosition,
        nBreakevenPosition: initialData?.nBreakevenPosition ?? StopLossTrailingsDefaults.nBreakevenPosition,
        nMarketBufferFix: initialData?.nMarketBufferFix ?? StopLossTrailingsDefaults.nMarketBufferFix,
        dMarketBufferSpread: initialData?.dMarketBufferSpread ?? StopLossTrailingsDefaults.dMarketBufferSpread,
        nStepsSize: initialData?.nStepsSize ?? StopLossTrailingsDefaults.nStepsSize,
        nStepsPosition: initialData?.nStepsPosition ?? StopLossTrailingsDefaults.nStepsPosition,
        nTimeCount: initialData?.nTimeCount ?? StopLossTrailingsDefaults.nTimeCount,
        nLinearStep: initialData?.nLinearStep ?? StopLossTrailingsDefaults.nLinearStep,
        nChangingDifference: initialData?.nChangingDifference ?? StopLossTrailingsDefaults.nChangingDifference,
        nHiLoPeriod: initialData?.nHiLoPeriod ?? StopLossTrailingsDefaults.nHiLoPeriod,
        bCaSupResFixPipsMin: initialData?.bCaSupResFixPipsMin ?? StopLossTrailingsDefaults.bCaSupResFixPipsMin,
        nCaSupResFixPipsMin: initialData?.nCaSupResFixPipsMin ?? StopLossTrailingsDefaults.nCaSupResFixPipsMin,
        bCaSupResRateOfChangeMin: initialData?.bCaSupResRateOfChangeMin ?? StopLossTrailingsDefaults.bCaSupResRateOfChangeMin,
        dCaSupResRateOfChangeMin: initialData?.dCaSupResRateOfChangeMin ?? StopLossTrailingsDefaults.dCaSupResRateOfChangeMin,
        bCaSupResFixPipsMax: initialData?.bCaSupResFixPipsMax ?? StopLossTrailingsDefaults.bCaSupResFixPipsMax,
        nCaSupResFixPipsMax: initialData?.nCaSupResFixPipsMax ?? StopLossTrailingsDefaults.nCaSupResFixPipsMax,
        bCaSupResRateOfChangeMax: initialData?.bCaSupResRateOfChangeMax ?? StopLossTrailingsDefaults.bCaSupResRateOfChangeMax,
        dCaSupResRateOfChangeMax: initialData?.dCaSupResRateOfChangeMax ?? StopLossTrailingsDefaults.dCaSupResRateOfChangeMax,
        nInosUsedForValueInjection: initialData?.nInosUsedForValueInjection ?? StopLossTrailingsDefaults.nInosUsedForValueInjection,
        nCaSupResUsedForValueInjection: initialData?.nCaSupResUsedForValueInjection ?? StopLossTrailingsDefaults.nCaSupResUsedForValueInjection,
      },
      enableReinitialize: true,
      validateOnChange: false,
      validateOnBlur: false,
      onSubmit: (values, { setSubmitting }) => {
        setTimeout(() => {
          try {
            onSave(values);
            setSuccess(true);
            setReturnMessage("Configuration saved successfully!");
            // autoCloseTimeoutRef.current = setTimeout(() => {
            //  handleClose();
            // }, 1500);
          } catch (err) {
            setError(true);
            setReturnMessage("Failed to save configuration.");
            console.error("Save error:", err);
          }
          setSubmitting(false);
        }, 100);
      },
    });

    const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
      formik;

    // ----------------------------------------------------------------------
    // Cross-validation logic
    // ----------------------------------------------------------------------
    useEffect(() => {
      const nCaSupResFixPipsMin = parseFloat(values.nCaSupResFixPipsMin);
      const dCaSupResRateOfChangeMin = parseFloat(values.dCaSupResRateOfChangeMin);
      const nCaSupResFixPipsMax = parseFloat(values.nCaSupResFixPipsMax);
      const dCaSupResRateOfChangeMax = parseFloat(values.dCaSupResRateOfChangeMax);
      const nStepsSize = parseFloat(values.nStepsSize);
      const nStepsPosition = parseFloat(values.nStepsPosition);

      // Check Chart Analyzer Fix Pips Min vs Max (only when Type is "5")
      if (
        values.bCaSupResFixPipsMin === true &&
        values.bCaSupResFixPipsMax === true &&
        values.nType === "5" &&
        !Number.isNaN(nCaSupResFixPipsMin) &&
        !Number.isNaN(nCaSupResFixPipsMax)
      ) {
        if (nCaSupResFixPipsMin >= nCaSupResFixPipsMax) {
          setError(true);
          setReturnMessage(
            <>
              <b>Support/resistance fix pips minimum</b> cannot be greater or equal to <b>Support/resistance fix pips maximum</b>!
            </>
          );
          return;
        }
      }

      // Check Chart Analyzer Rate of Change Min vs Max (only when Type is "5")
      if (
        values.bCaSupResRateOfChangeMin === true &&
        values.bCaSupResRateOfChangeMax === true &&
        values.nType === "5" &&
        !Number.isNaN(dCaSupResRateOfChangeMin) &&
        !Number.isNaN(dCaSupResRateOfChangeMax)
      ) {
        if (dCaSupResRateOfChangeMin >= dCaSupResRateOfChangeMax) {
          setError(true);
          setReturnMessage(
            <>
              <b>Support/resistance rate of change minimum</b> cannot be greater or equal to <b>Support/resistance rate of change maximum</b>!
            </>
          );
          return;
        }
      }

      // Check Steps Size vs Steps Position (only when Type is "6")
      if (values.nType === "6" && !Number.isNaN(nStepsSize) && !Number.isNaN(nStepsPosition)) {
        if (nStepsSize >= nStepsPosition) {
          setError(true);
          setReturnMessage(
            <>
              <b>Steps size</b> cannot be greater or equal to <b>Steps position</b>!
            </>
          );
          return;
        }
      }

      // If all validations pass, clear error state
      setError(false);
      setReturnMessage("");
    }, [
      values.nType,
      values.bCaSupResFixPipsMin,
      values.nCaSupResFixPipsMin,
      values.bCaSupResRateOfChangeMin,
      values.dCaSupResRateOfChangeMin,
      values.bCaSupResFixPipsMax,
      values.nCaSupResFixPipsMax,
      values.bCaSupResRateOfChangeMax,
      values.dCaSupResRateOfChangeMax,
      values.nStepsSize,
      values.nStepsPosition,
    ]);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClickOpen = (scrollType) => () => {
      setOpen(true);
      setScroll(scrollType);
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    let showWarning = false;
    if (initialStopLoss.nSelection === "0") {
      showWarning = true;
    }

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    let saveAndCloseDisabled = false;
    if (values.nType === "4" && indicators && indicators.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nType === "4" &&
      (values.nInosUsedForValueInjection === "" || values.nInosUsedForValueInjection === "0" || values.nInosUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }
    if (values.nType === "5" && chartAnalyzers && chartAnalyzers.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nType === "5" &&
      (values.nCaSupResUsedForValueInjection === "" || values.nCaSupResUsedForValueInjection === "0" || values.nCaSupResUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }

    const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
    if (hasValidationErrors()) {
      saveAndCloseDisabled = true;
    }

    // Disable save button if cross-validation fails
    if (error) {
      saveAndCloseDisabled = true;
    }

    // ----------------------------------------------------------------------
    // Main component
    // ----------------------------------------------------------------------
    return (
      <>
        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
          Configure stop loss trailing
        </B1Tiny>

        <ModalDialog
          open={open}
          handleClose={handleClose}
          scroll={scroll}
          outlet={
            <>
              <DialogTitle
                id="dialog-title"
                sx={{
                  background: "#F2F2F2",
                  textTransform: "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignContent: "center",
                  py: 1,
                  pl: 2,
                  pr: 1,
                }}
              >
                <Typography variant="contentBig">Configure stop loss trailing</Typography>

                <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                  <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
                </IconButton>
              </DialogTitle>

              <FormikProvider value={formik}>
                <DialogContent
                  dividers
                  sx={{
                    background: "#F2F2F2",
                    borderTop: "1px solid #C8C8C8",
                    borderBottom: "0px solid #C8C8C8",
                    px: 2,
                    pt: 2,
                    pb: 1,
                    width: "100%",
                    boxSizing: "border-box",
                    overflowY: "scroll",
                    overflowX: "hidden",
                    // Custom scrollbar styling
                    "&::-webkit-scrollbar": {
                      width: "16px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#e0e0e0",
                      borderRadius: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888888",
                      borderRadius: "6px",
                      border: "2px solid #e0e0e0",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#666666",
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: "thin",
                    scrollbarColor: "#888888 #e0e0e0",
                  }}
                >
                  <Stack spacing={2}>
                    {showWarning ? (
                      <Box>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          The stop loss trailing feature is dependent on configuring an initial stop loss. If this condition is not met, the feature
                          remains inactive, and no stop loss will be applied.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nChangePosition */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Activation position"
                        helpReference="HandleStopLossTrailings"
                        helpText=""
                        helpLink=""
                        fieldName="nChangePosition"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nChangePosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Help Sections */}
                    {/* ---------------------------------------------------------------------- */}
                    {STOP_LOSS_TRAILING_ACTIVATION_POSITION_HELP_DESCRIPTION && (
                      <Box>
                        <B3Tiny
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCollapseActivation(!collapseActivation);
                          }}
                        >
                          {collapseActivation ? "Hide help section" : "Show help section"}
                        </B3Tiny>

                        <Collapse sx={{ p: "3px" }} in={collapseActivation} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                              {STOP_LOSS_TRAILING_ACTIVATION_POSITION_HELP_DESCRIPTION}
                              <br />
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              borderRadius: "10px",
                              py: 2,
                              boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                              backgroundColor: "white",
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={STOP_LOSS_TRAILING_ACTIVATION_POSITION_HELP_IMAGE}
                              alt="Stop loss trailing activation position"
                              sx={{
                                objectFit: "contain",
                                maxHeight: "619px",
                              }}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nType */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Type"
                        helpReference="HandleStopLossTrailings"
                        helpText=""
                        helpLink=""
                        fieldName="nType"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select {...getFieldProps("nType")} input={<ModalContentOutlinedInput />} error={Boolean(touched.nType && errors.nType)}>
                          <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Fix pips from inital stop loss</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Breakeven</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">Market</ModalContentMenuItem>
                          {professionalMode === true || values.nType === "4" ? (
                            <ModalContentMenuItem value="4">Indicator value</ModalContentMenuItem>
                          ) : null}
                          {professionalMode === true || values.nType === "5" ? (
                            <ModalContentMenuItem value="5">Support/resistance from chart analyzer</ModalContentMenuItem>
                          ) : null}
                          <ModalContentMenuItem value="6">Stepwise increase/decrease</ModalContentMenuItem>
                          <ModalContentMenuItem value="7">Closing after specified time period</ModalContentMenuItem>
                          <ModalContentMenuItem value="8">Linear increase/decrease</ModalContentMenuItem>
                          <ModalContentMenuItem value="9">Dynamic increase/decrease</ModalContentMenuItem>
                          <ModalContentMenuItem value="10">Last high/low</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Help Sections */}
                    {/* ---------------------------------------------------------------------- */}
                    {STOP_LOSS_TRAILING_TYPES_HELP_SECTION[values.nType] && (
                      <Box>
                        <B3Tiny
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCollapseType(!collapseType);
                          }}
                        >
                          {collapseType ? "Hide help section" : "Show help section"}
                        </B3Tiny>

                        <Collapse sx={{ p: "3px" }} in={collapseType} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                              {STOP_LOSS_TRAILING_TYPES_HELP_SECTION[values.nType].description}
                              <br />
                            </Typography>
                          </Box>

                          {STOP_LOSS_TRAILING_TYPES_HELP_SECTION[values.nType].image !== null ? (
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={STOP_LOSS_TRAILING_TYPES_HELP_SECTION[values.nType].image}
                                alt="Stop loss trailing type"
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: STOP_LOSS_TRAILING_TYPES_HELP_SECTION[values.nType].imageHeight,
                                }}
                              />
                            </Box>
                          ) : null}
                        </Collapse>
                      </Box>
                    )}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nBreakevenPosition */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "2" ? (
                      <Box>
                        <SmartParameter
                          value="Breakeven position"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nBreakevenPosition"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nBreakevenPosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMarketBufferFix */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market buffer fix"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nMarketBufferFix"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nMarketBufferFix" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dMarketBufferSpread */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market buffer spread"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="dMarketBufferSpread"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="dMarketBufferSpread" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nInosUsedForValueInjection */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "4" &&
                    (() => {
                      const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                      return forwardIndicators.length > 0;
                    })() ? (
                      <Box>
                        <SmartParameter
                          value="Used indicator"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nInosUsedForValueInjection"
                          touched={touched}
                          errors={errors}
                        />
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nInosUsedForValueInjection")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nInosUsedForValueInjection && errors.nInosUsedForValueInjection)}
                          >
                            <ModalContentMenuItem value="0">
                              <Box>
                                <Typography variant="contentSmall">Please select ...</Typography>
                              </Box>
                            </ModalContentMenuItem>
                            {indicators
                              ?.filter((indicator) => indicator.config?.nSignalUsedFor === "2")
                              .map((indicator) => (
                                <ModalContentMenuItem key={indicator.id.toString()} value={indicator.id.toString()}>
                                  <Box>
                                    <Typography variant="contentSmall">
                                      {indicator.name} ({indicator.subtitle})
                                    </Typography>
                                  </Box>
                                </ModalContentMenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    {values.nType === "4" &&
                    (() => {
                      const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                      return forwardIndicators.length === 0;
                    })() ? (
                      <Box sx={{ m: 0, p: 0 }}>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          No indicator with <b>Signal used for = Forward</b> is available. Please add at least one indicator and set its{" "}
                          <b>Signal used for</b> setting to <b>Forward</b>.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nCaSupResUsedForValueInjection */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "5" &&
                    (() => {
                      const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                      return forwardChartAnalyzers.length > 0;
                    })() ? (
                      <Box>
                        <SmartParameter
                          value="Used chart analyzer"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nCaSupResUsedForValueInjection"
                          touched={touched}
                          errors={errors}
                        />
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nCaSupResUsedForValueInjection")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nCaSupResUsedForValueInjection && errors.nCaSupResUsedForValueInjection)}
                          >
                            <ModalContentMenuItem value="0">
                              <Box>
                                <Typography variant="contentSmall">Please select ...</Typography>
                              </Box>
                            </ModalContentMenuItem>
                            {chartAnalyzers
                              ?.filter((chartAnalyzer) => chartAnalyzer.config?.nSignalUsedFor === "2")
                              .map((chartAnalyzer) => (
                                <ModalContentMenuItem key={chartAnalyzer.id} value={chartAnalyzer.id.toString()}>
                                  <Box>
                                    <Typography variant="contentSmall">
                                      {chartAnalyzer.name} ({chartAnalyzer.subtitle})
                                    </Typography>
                                  </Box>
                                </ModalContentMenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    {values.nType === "5" &&
                    (() => {
                      const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                      return forwardChartAnalyzers.length === 0;
                    })() ? (
                      <Box sx={{ m: 0, p: 0 }}>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          No chart analyzer with <b>Signal used for = Forward</b> is available. Please add at least one chart analyzer and set its{" "}
                          <b>Signal used for</b> setting to <b>Forward</b>.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {values.nType === "5" ? (
                      <>
                        {professionalMode === true ? (
                          <>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Divider */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Grid */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* nFixPipsMin */}
                              {/* ---------------------------------------------------------------------- */}
                              <Box>
                                <SmartParameter
                                  value="Support/resistance fix pips minimum"
                                  helpReference="HandleStopLossTrailings"
                                  helpText=""
                                  helpLink=""
                                  fieldName="nCaSupResFixPipsMin"
                                  touched={touched}
                                  errors={errors}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <ModalContentSwitch
                                    sx={{ my: "12px" }}
                                    checked={values.bCaSupResFixPipsMin === true}
                                    onChange={(event) => {
                                      setFieldValue("bCaSupResFixPipsMin", event.target.checked);
                                    }}
                                  />
                                  {values.bCaSupResFixPipsMin === true ? (
                                    <SmartNumberInput textFieldName="nCaSupResFixPipsMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                  ) : null}
                                </Box>
                              </Box>

                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* dCaSupResRateOfChangeMin */}
                              {/* ---------------------------------------------------------------------- */}
                              <Box>
                                <SmartParameter
                                  value="Support/resistance rate of change minimum"
                                  helpReference="HandleStopLossTrailings"
                                  helpText=""
                                  helpLink=""
                                  fieldName="dCaSupResRateOfChangeMin"
                                  touched={touched}
                                  errors={errors}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <ModalContentSwitch
                                    sx={{ my: "12px" }}
                                    checked={values.bCaSupResRateOfChangeMin === true}
                                    onChange={(event) => {
                                      setFieldValue("bCaSupResRateOfChangeMin", event.target.checked);
                                    }}
                                  />
                                  {values.bCaSupResRateOfChangeMin === true ? (
                                    <SmartNumberInput textFieldName="dCaSupResRateOfChangeMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Divider */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Grid */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                              }}
                            >
                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* nFixPipsMax */}
                              {/* ---------------------------------------------------------------------- */}
                              <Box>
                                <SmartParameter
                                  value="Support/resistance fix pips maximum"
                                  helpReference="HandleStopLossTrailings"
                                  helpText=""
                                  helpLink=""
                                  fieldName="nCaSupResFixPipsMax"
                                  touched={touched}
                                  errors={errors}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <ModalContentSwitch
                                    sx={{ my: "12px" }}
                                    checked={values.bCaSupResFixPipsMax === true}
                                    onChange={(event) => {
                                      setFieldValue("bCaSupResFixPipsMax", event.target.checked);
                                    }}
                                  />
                                  {values.bCaSupResFixPipsMax === true ? (
                                    <SmartNumberInput textFieldName="nCaSupResFixPipsMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                  ) : null}
                                </Box>
                              </Box>

                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* dCaSupResRateOfChangeMax */}
                              {/* ---------------------------------------------------------------------- */}
                              <Box>
                                <SmartParameter
                                  value="Support/resistance rate of change maximum"
                                  helpReference="HandleStopLossTrailings"
                                  helpText=""
                                  helpLink=""
                                  fieldName="dCaSupResRateOfChangeMax"
                                  touched={touched}
                                  errors={errors}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <ModalContentSwitch
                                    sx={{ my: "12px" }}
                                    checked={values.bCaSupResRateOfChangeMax === true}
                                    onChange={(event) => {
                                      setFieldValue("bCaSupResRateOfChangeMax", event.target.checked);
                                    }}
                                  />
                                  {values.bCaSupResRateOfChangeMax === true ? (
                                    <SmartNumberInput textFieldName="dCaSupResRateOfChangeMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>
                          </>
                        ) : null}
                      </>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nStepsSize */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "6" ? (
                      <Box>
                        <SmartParameter
                          value="Steps size"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nStepsSize"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nStepsSize" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nStepsPosition */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "6" ? (
                      <Box>
                        <SmartParameter
                          value="Steps position"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nStepsPosition"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nStepsPosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nTimeCount */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "7" ? (
                      <Box>
                        <SmartParameter
                          value="Time count"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nTimeCount"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nTimeCount" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nLinearStep */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "8" ? (
                      <Box>
                        <SmartParameter
                          value="Linear step"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nLinearStep"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nLinearStep" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nChangingDifference */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "9" ? (
                      <Box>
                        <SmartParameter
                          value="Changing difference"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nChangingDifference"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nChangingDifference" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nHiLoPeriod */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "10" ? (
                      <Box>
                        <SmartParameter
                          value="High/low period"
                          helpReference="HandleStopLossTrailings"
                          helpText=""
                          helpLink=""
                          fieldName="nHiLoPeriod"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nHiLoPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}
                  </Stack>
                </DialogContent>

                <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                  <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
                </Box>

                <DialogActions
                  sx={{
                    background: "#F2F2F2",
                    p: 2,
                    pt: 0,
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleReset}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Reset
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      disabled={saveAndCloseDisabled}
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Save
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleClose}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Close
                    </ModalActionsLoadingButton>
                  </Box>
                </DialogActions>
              </FormikProvider>
            </>
          }
        />
      </>
    );
  }
);
